import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { RepoService } from '../../../services/repo.service';
import type {
  SdtmJobRef,
  SdtmSnapshot,
  SdtmStepName,
  SdtmStudyDetail,
  SdtmStudyListResponse,
  SdtmUploadSlot,
} from '../models/sdtm-study.model';
import type { CodegenLanguage, DefineWorkbook, SdtmSpec } from '../models/sdtm-study.model';

/**
 * API client for the unified SDTM study pipeline (`/workflows/sdtm/studies`).
 *
 * INTERIM: typed against the hand-written contract in `sdtm-study.model.ts`.
 * The Bearer token is attached by the global auth interceptor; like the other
 * SDTM services we pass the active repo's `workspace_server_url` so the backend
 * can resolve the workspace for local repos.
 *
 * NOTE: this is a SEPARATE aggregate from the annotation-namespace
 * `StudiesApiService` (`/workflows/sdtm/annotation/studies`). Different id space.
 */
@Injectable({ providedIn: 'root' })
export class SdtmStudiesApiService {
  private readonly http = inject(HttpClient);
  private readonly repo = inject(RepoService);
  private readonly baseUrl = `${environment.backendUrl}/workflows/sdtm/studies`;

  private get workspaceServerUrl(): string | null {
    return this.repo.repo()?.url ?? null;
  }

  private wsParams(): HttpParams {
    let params = new HttpParams();
    const wsUrl = this.workspaceServerUrl;
    if (wsUrl) params = params.set('workspace_server_url', wsUrl);
    return params;
  }

  list(): Observable<SdtmStudyListResponse> {
    return this.http.get<SdtmStudyListResponse>(this.baseUrl, {
      params: this.wsParams(),
      withCredentials: true,
    });
  }

  /** Poll-on-read: this endpoint advances running steps server-side. */
  get(id: string): Observable<SdtmStudyDetail> {
    return this.http.get<SdtmStudyDetail>(`${this.baseUrl}/${id}`, {
      params: this.wsParams(),
      withCredentials: true,
    });
  }

  create(body: {
    name: string;
    description?: string;
    sponsor?: string;
    protocol_id?: string;
    phase?: string;
    ig_version?: string;
    msg_version?: string;
    ct_version?: string;
    repo_id?: number;
  }): Observable<SdtmStudyDetail> {
    // Pass workspace_server_url (like every sibling call) so the backend can
    // resolve a local repo's workspace and provision sdtmct.csv / req_columns
    // at create time — without it, provisioning is silently skipped.
    return this.http.post<SdtmStudyDetail>(this.baseUrl, body, {
      params: this.wsParams(),
      withCredentials: true,
    });
  }

  patch(id: string, body: Record<string, unknown>): Observable<SdtmStudyDetail> {
    return this.http.patch<SdtmStudyDetail>(`${this.baseUrl}/${id}`, body, {
      withCredentials: true,
    });
  }

  delete(id: string): Observable<void> {
    // Pass the workspace URL so the backend can also delete the study's
    // workspace folder (sdtm-studies/{id}), not just the DB record.
    return this.http.delete<void>(`${this.baseUrl}/${id}`, {
      params: this.wsParams(),
      withCredentials: true,
    });
  }

  /**
   * Upload a file into a typed slot. The backend returns a FileManifestItem
   * ({slot, filename, path}) — NOT the study detail; do not treat it as one.
   */
  uploadFile(
    id: string,
    slot: SdtmUploadSlot,
    file: File,
  ): Observable<{ slot: string; filename: string; path: string }> {
    const form = new FormData();
    form.append('slot', slot);
    form.append('file', file, file.name);
    return this.http.post<{ slot: string; filename: string; path: string }>(
      `${this.baseUrl}/${id}/files`,
      form,
      { params: this.wsParams(), withCredentials: true },
    );
  }

  /** List already-uploaded input files grouped by slot. */
  /** Delete one uploaded input file from a slot. */
  deleteFile(id: string, slot: string, filename: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}/files`, {
      params: this.wsParams().set('slot', slot).set('filename', filename),
      withCredentials: true,
    });
  }

  listFiles(id: string): Observable<{ slot: string; filename: string; path: string }[]> {
    return this.http.get<{ slot: string; filename: string; path: string }[]>(
      `${this.baseUrl}/${id}/files`,
      { params: this.wsParams(), withCredentials: true },
    );
  }

  /**
   * Read an already-uploaded input file's raw bytes straight from the
   * workspace (`GET {workspace_server_url}/read-file?file_path=...`) —
   * mirrors `Node.getBlob()` in the file explorer, but via `HttpClient` so
   * the global `AuthInterceptor` attaches the bearer token (it matches on
   * the `currentRepoUrl` prefix). `path` is a `FileManifestItem.path` from
   * `listFiles`, already workspace-root-relative. Used by the trial-design
   * side panel to re-upload a study's uploaded USDM file to its own job API.
   *
   * No `withCredentials` here (unlike the backend calls): this request goes
   * straight to the workspace server, which authenticates via the bearer
   * header (not cookies) and returns a wildcard `Access-Control-Allow-Origin`.
   * A credentialed request would be rejected by the browser against `*`.
   */
  downloadInputFile(path: string): Observable<Blob> {
    const wsUrl = this.workspaceServerUrl;
    if (!wsUrl) {
      throw new Error('No active workspace to read the file from.');
    }
    const base = wsUrl.endsWith('/') ? wsUrl : `${wsUrl}/`;
    return this.http.get(`${base}read-file`, {
      params: new HttpParams().set('file_path', path),
      responseType: 'blob',
    });
  }

  /**
   * Upload manually-prepared trial-design tables (TA/TE/TV/TI/TS) straight to
   * the workspace server (`POST {workspace_server_url}/upload-file`), landing
   * them at `sdtm-studies/{id}/trial-design/manual/outputs/` — the same
   * trial-design output tree the generated USDM run writes to, which codegen
   * reads. Mirrors the file explorer's direct upload (files + path form fields,
   * bearer via the interceptor, no `withCredentials`); no backend endpoint.
   * Used when the study has no USDM and the user supplies the tables directly.
   */
  uploadTrialDesignDatasets(id: string, files: File[]): Observable<unknown> {
    const wsUrl = this.workspaceServerUrl;
    if (!wsUrl) {
      throw new Error('No active workspace to upload the datasets to.');
    }
    const base = wsUrl.endsWith('/') ? wsUrl : `${wsUrl}/`;
    const form = new FormData();
    for (const f of files) form.append('files', f, f.name);
    form.append('path', `sdtm-studies/${id}/trial-design/manual/outputs`);
    return this.http.post(`${base}upload-file`, form);
  }

  /** Names of the manually uploaded trial-design CSVs (see `uploadTrialDesignDatasets`). */
  listTrialDesignDatasets(id: string): Observable<string[]> {
    const wsUrl = this.workspaceServerUrl;
    if (!wsUrl) return of([]);
    const base = wsUrl.endsWith('/') ? wsUrl : `${wsUrl}/`;
    const dir = `sdtm-studies/${id}/trial-design/manual/outputs/`;
    return this.http.get<{ files?: string[] }>(`${base}tree`).pipe(
      map((t) =>
        (t.files ?? [])
          .map((f) => {
            const i = f.indexOf(dir);
            return i < 0 ? '' : f.slice(i + dir.length);
          })
          .filter((n) => n && !n.includes('/'))
          .sort(),
      ),
    );
  }

  /** Delete one manually uploaded trial-design CSV from the workspace. */
  deleteTrialDesignDataset(id: string, name: string): Observable<unknown> {
    const wsUrl = this.workspaceServerUrl;
    if (!wsUrl) {
      throw new Error('No active workspace to delete the dataset from.');
    }
    const base = wsUrl.endsWith('/') ? wsUrl : `${wsUrl}/`;
    return this.http.post(`${base}delete`, null, {
      params: { path: `sdtm-studies/${id}/trial-design/manual/outputs/${name}` },
    });
  }

  /**
   * True once this study has any trial-design output CSVs in the workspace —
   * `sdtm-studies/{id}/trial-design/**‍/outputs/*.csv` — covering BOTH the
   * generated USDM run and manual uploads (same glob codegen uses). Reads the
   * repo server's flat `/tree` listing directly. Used to gate the annotation
   * step: it stays locked until trial design has been generated or uploaded.
   * Any failure resolves to `false` (treated as not-done) rather than throwing.
   */
  hasTrialDesignOutputs(id: string): Observable<boolean> {
    const wsUrl = this.workspaceServerUrl;
    if (!wsUrl) return of(false);
    const base = wsUrl.endsWith('/') ? wsUrl : `${wsUrl}/`;
    return this.http.get<{ files?: string[] }>(`${base}tree`).pipe(
      map((t) =>
        (t.files ?? []).some(
          (f) =>
            f.includes(id) &&
            f.includes('/trial-design/') &&
            f.includes('/outputs/') &&
            f.toLowerCase().endsWith('.csv'),
        ),
      ),
      catchError(() => of(false)),
    );
  }

  completeUpload(id: string): Observable<SdtmStudyDetail> {
    return this.http.post<SdtmStudyDetail>(
      `${this.baseUrl}/${id}/steps/upload/complete`,
      {},
      { params: this.wsParams(), withCredentials: true },
    );
  }

  getSnapshot(id: string, step: SdtmStepName): Observable<SdtmSnapshot> {
    return this.http.get<SdtmSnapshot>(`${this.baseUrl}/${id}/snapshots/${step}`, {
      params: this.wsParams(),
      withCredentials: true,
    });
  }

  /** CRF forms parsed from the study's uploaded ALS (for test-mode selection). */
  listAnnotationForms(
    id: string,
  ): Observable<{ items: { FormOID: string; DraftFormName?: string | null; field_count?: number }[] }> {
    return this.http.get<{ items: { FormOID: string; DraftFormName?: string | null; field_count?: number }[] }>(
      `${this.baseUrl}/${id}/annotation/forms`,
      { params: this.wsParams(), withCredentials: true },
    );
  }

  runAnnotation(
    id: string,
    opts?: { annotation_mode?: 'raw' | 'sdtm'; selected_form_oids?: string[] },
  ): Observable<SdtmJobRef> {
    return this.http.post<SdtmJobRef>(
      `${this.baseUrl}/${id}/steps/annotation/run`,
      opts ?? {},
      { params: this.wsParams(), withCredentials: true },
    );
  }

  /** The uploaded Define-XML spec workbook parsed to tab-per-sheet (spec viewer). */
  getDefineSpec(id: string): Observable<DefineWorkbook> {
    return this.http.get<DefineWorkbook>(`${this.baseUrl}/${id}/spec/define`, {
      params: this.wsParams(),
      withCredentials: true,
    });
  }

  /** The generated spec as a real multi-sheet Define-XML .xlsx (download). */
  getDefineXlsx(id: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/${id}/spec/define.xlsx`, {
      params: this.wsParams(),
      withCredentials: true,
      responseType: 'blob',
    });
  }

  /** Approve the (edited) spec → writes spec.yaml, unlocks codegen. */
  approveSpec(id: string, spec: SdtmSpec): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${id}/steps/spec_validation/complete`, spec, {
      params: this.wsParams(),
      withCredentials: true,
    });
  }

  /** Launch a codegen job for one domain in R or SAS. Returns the SdtmCodeJob id to stream. */
  runCodegen(id: string, domain: string, language: CodegenLanguage = 'r', draftSource?: string): Observable<SdtmJobRef> {
    return this.http.post<SdtmJobRef>(
      `${this.baseUrl}/${id}/steps/codegen/run`,
      { domain, language, ...(draftSource !== undefined ? { draft_source: draftSource } : {}) },
      { params: this.wsParams(), withCredentials: true },
    );
  }

  /** Stop a running codegen job (SdtmCodeJob id from runCodegen / the snapshot). */
  cancelCodegen(jobId: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(
      `${environment.backendUrl}/workflows/sdtm/code-generation/jobs/${jobId}/cancel`,
      {},
      { withCredentials: true },
    );
  }

  /**
   * Download a generated codegen artifact (`script` = the R/SAS program, `derived` = SDTM
   * dataset CSV, `cdisc_findings` = validation findings JSON) for one domain's
   * SdtmCodeJob. Goes through HttpClient (auth interceptor) as a blob; passes
   * the workspace url so local repos resolve. The Content-Disposition filename
   * is set server-side.
   */
  downloadCodegenArtifact(
    jobId: string,
    artifact: 'script' | 'derived' | 'cdisc_findings',
  ): Observable<Blob> {
    return this.http.get(
      `${environment.backendUrl}/workflows/sdtm/code-generation/jobs/${jobId}/outputs/${artifact}`,
      { params: this.wsParams(), withCredentials: true, responseType: 'blob' },
    );
  }

  completeStudy(id: string): Observable<SdtmStudyDetail> {
    return this.http.post<SdtmStudyDetail>(
      `${this.baseUrl}/${id}/complete`,
      {},
      { params: this.wsParams(), withCredentials: true },
    );
  }
}
